// src/components/clients/MyFleet.js
import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Badge, FuelBar, Modal, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { TRUCK_STATUSES } from '../../data/store';
import { formatDuration, formatETATime } from '../../services/geocoding';
import TripView from './TripView';

export default function MyFleet({ onNavigateToMap }) {
  const { trucks, gpsDevices, documents, currentUser } = useApp();
  const { isMobile } = useResponsive();
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [tripView, setTripView] = useState(null); // { truck, shipment }

  const myTrucks = trucks.filter(t => t.clientId === currentUser?.clientId);
  const myDocs = documents.filter(d => myTrucks.some(t => t.id === d.truckId));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary Stats */}
      <ResponsiveGrid columns={4} tabletColumns={2} mobileColumns={2} gap={isMobile ? 10 : 14}>
        {[
          { label: 'Total Trucks', value: myTrucks.length, color: '#3b82f6', icon: '🚛' },
          { label: 'Moving', value: myTrucks.filter(t => t.status === 'moving').length, color: '#10b981', icon: '▶' },
          { label: 'Idle', value: myTrucks.filter(t => t.status === 'idle').length, color: '#f59e0b', icon: '⏸' },
          { label: 'Shipments', value: myDocs.length, color: '#8b5cf6', icon: '📄' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bgCard)', border: `1px solid ${s.color}22`, borderRadius: 14, padding: isMobile ? '14px 16px' : '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: s.color + '18', color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{s.icon}</div>
            </div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? 22 : 28, color: 'var(--textPrimary)', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
          </div>
        ))}
      </ResponsiveGrid>

      {/* Truck Cards Grid */}
      <div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--textPrimary)', marginBottom: 14 }}>My Fleet</div>
        {myTrucks.length === 0 ? (
          <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 14, padding: '48px', textAlign: 'center', color: 'var(--textFaint)', fontSize: 14 }}>
            No trucks assigned to your account yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {myTrucks.map(truck => {
              const status = TRUCK_STATUSES[truck.status] || TRUCK_STATUSES.offline;
              const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
              const truckDocs = documents.filter(d => d.truckId === truck.id);
              const activeShipment = [...truckDocs]
                .filter(d => d.status === 'in-transit' && d.etaMinutes != null)
                .sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0))[0];
              return (
                <div
                  key={truck.id}
                  onClick={() => setSelectedTruck(truck)}
                  style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', overflow: 'hidden' }}
                  onMouseEnter={e => { e.currentTarget.style.border = `1px solid ${truck.color}44`; e.currentTarget.style.boxShadow = `0 0 24px ${truck.color}12`; }}
                  onMouseLeave={e => { e.currentTarget.style.border = '1px solid var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {/* Color bar */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: truck.color, borderRadius: '14px 14px 0 0' }} />

                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: 'var(--textPrimary)' }}>{truck.plateNumber}</div>
                      <div style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 2 }}>{truck.model} {truck.year} · {truck.type}</div>
                    </div>
                    <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                  </div>

                  {activeShipment && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, marginBottom: 12 }}>
                      <span style={{ fontSize: 14 }}>🕐</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: '#60a5fa', fontWeight: 600 }}>ETA {formatETATime(activeShipment.etaTimestamp)} · {formatDuration(activeShipment.etaMinutes)}</div>
                        <div style={{ fontSize: 10, color: 'var(--textFaint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>To {activeShipment.destination}</div>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); setTripView({ truck, shipment: activeShipment }); }}
                        style={{ flexShrink: 0, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6, color: '#60a5fa', fontSize: 10, fontWeight: 700, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        🗺️ View Trip
                      </button>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    <div style={{ padding: '8px 10px', background: 'var(--bgInput)', borderRadius: 8 }}>
                      <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Driver</div>
                      <div style={{ fontSize: 12, color: 'var(--textSecondary)', fontWeight: 500 }}>{truck.driver || '—'}</div>
                    </div>
                    <div style={{ padding: '8px 10px', background: 'var(--bgInput)', borderRadius: 8 }}>
                      <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Speed</div>
                      <div style={{ fontSize: 12, color: truck.status === 'moving' ? '#10b981' : 'var(--textSecondary)', fontWeight: 500 }}>
                        {truck.status === 'moving' ? `${truck.location?.speed || 0} km/h` : '0 km/h'}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>Fuel Level</div>
                    <FuelBar level={truck.fuelLevel || 0} />
                  </div>

                  <div style={{ fontSize: 11, color: 'var(--textMuted)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 12 }}>
                    <span>📍</span>
                    <span>{truck.location?.address || 'Unknown location'}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
                    <Badge color="#6b7a9a">{truckDocs.length} docs</Badge>
                    {truck.status === 'moving' && (
                      <button
                        onClick={e => { e.stopPropagation(); onNavigateToMap?.(); }}
                        style={{ marginLeft: 'auto', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, color: '#34d399', fontSize: 11, fontWeight: 600, padding: '3px 10px', cursor: 'pointer' }}
                      >
                        Track Live
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Documents */}
      {myDocs.length > 0 && (
        <Card title="Recent Shipments">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {myDocs.slice(0, 6).map(doc => {
              const truck = myTrucks.find(t => t.id === doc.truckId);
              const statusColors = { pending: '#f59e0b', 'in-transit': '#3b82f6', delivered: '#10b981', cancelled: '#ef4444' };
              return (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: truck?.color || '#3b82f6', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textSecondary)' }}>{doc.docNumber}</div>
                      <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{doc.type} · {doc.origin} → {doc.destination}</div>
                      {doc.status === 'in-transit' && doc.etaMinutes != null && (
                        <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 2 }}>🕐 ETA {formatETATime(doc.etaTimestamp)} ({formatDuration(doc.etaMinutes)})</div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>{doc.date}</span>
                    <Badge color={statusColors[doc.status] || '#6b7280'}>{doc.status}</Badge>
                    {(doc.status === 'in-transit' || doc.status === 'delivered') && truck && (
                      <button
                        onClick={() => setTripView({ truck, shipment: doc })}
                        style={{ background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--textMuted)', fontSize: 10, fontWeight: 600, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        🗺️ Trip
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Truck Detail Modal */}
      <Modal open={!!selectedTruck} onClose={() => setSelectedTruck(null)} title="Truck Details" width={520}>
        {selectedTruck && (() => {
          const status = TRUCK_STATUSES[selectedTruck.status] || TRUCK_STATUSES.offline;
          const gps = gpsDevices.find(g => g.id === selectedTruck.gpsDeviceId);
          const truckDocs = documents.filter(d => d.truckId === selectedTruck.id);
          return (
            <div>
              <div style={{ padding: '14px 16px', background: 'var(--bgInput)', borderRadius: 12, border: '1px solid var(--borderInput)', marginBottom: 18, borderLeft: `4px solid ${selectedTruck.color}` }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--textPrimary)' }}>{selectedTruck.plateNumber}</div>
                <div style={{ fontSize: 13, color: 'var(--textMuted)', marginTop: 2 }}>{selectedTruck.model} {selectedTruck.year} · {selectedTruck.capacity}</div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                  {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Driver', value: selectedTruck.driver },
                  { label: 'Driver Phone', value: selectedTruck.driverPhone },
                  { label: 'Current Speed', value: selectedTruck.status === 'moving' ? `${selectedTruck.location?.speed || 0} km/h` : 'Stationary' },
                  { label: 'Heading', value: selectedTruck.location?.heading || '—' },
                  { label: 'Location', value: selectedTruck.location?.address },
                  { label: 'Mileage', value: selectedTruck.mileage },
                  { label: 'Last Service', value: selectedTruck.lastService },
                  { label: 'GPS Device', value: gps ? `${gps.deviceId} (${gps.model})` : 'No GPS' },
                ].map(item => (
                  <div key={item.label} style={{ padding: '10px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Fuel Level</div>
                <FuelBar level={selectedTruck.fuelLevel || 0} />
              </div>

              {truckDocs.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Shipment History ({truckDocs.length})</div>
                  {truckDocs.map(doc => {
                    const sc = { pending: '#f59e0b', 'in-transit': '#3b82f6', delivered: '#10b981', cancelled: '#ef4444' };
                    return (
                      <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 6 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--textSecondary)' }}>{doc.docNumber}</div>
                          <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{doc.origin} → {doc.destination}</div>
                          {doc.status === 'in-transit' && doc.etaMinutes != null && (
                            <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 2 }}>🕐 ETA {formatETATime(doc.etaTimestamp)} ({formatDuration(doc.etaMinutes)})</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                          <Badge color={sc[doc.status] || '#6b7280'}>{doc.status}</Badge>
                          {(doc.status === 'in-transit' || doc.status === 'delivered') && (
                            <button
                              onClick={() => setTripView({ truck: selectedTruck, shipment: doc })}
                              style={{ background: 'var(--bgHover)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--textMuted)', fontSize: 10, fontWeight: 600, padding: '4px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              🗺️ Trip
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setSelectedTruck(null); onNavigateToMap?.(); }}
                  style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 9, color: '#34d399', fontSize: 13, fontWeight: 600, padding: '9px 18px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                >
                  📍 Track on Map
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Trip Trail View */}
      {tripView && (
        <TripView
          truck={tripView.truck}
          shipment={tripView.shipment}
          onClose={() => setTripView(null)}
        />
      )}
    </div>
  );
}
